/**
 * 组件截图查看页：在浏览器中访问并显示后端生成的组件截图（image/jpeg）。
 * 供外部直接打开或分享链接使用。
 *
 * 用法：
 * - 本页：/store-revenue-monitor/screenshot-viewer?hotelId=45&componentType=room-status
 * - 直接打开接口（仅图片）：/api/store-revenue/component-screenshot?hotelId=45&componentType=room-status
 */
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InputNumber, Input, Select, Button, Space, Typography, Alert } from "antd";

const COMPONENT_OPTIONS = [
  { value: "room-status", label: "实时房态" },
  { value: "hourly-price-by-room", label: "近期房型房价" },
  { value: "room-status-recently", label: "远期房态" },
  { value: "channel-price", label: "渠道房价" },
  { value: "order-traffic", label: "订单流速" },
  { value: "revpar", label: "可售房收入" },
  { value: "room-health", label: "房态健康" },
  { value: "customer-source", label: "客源结构" },
  { value: "discount-analysis", label: "折扣率" },
  { value: "channel-analysis", label: "渠道订单" },
  { value: "channel-info", label: "渠道收入" },
  { value: "occupancy-analysis", label: "出租率" },
  { value: "operation-efficiency", label: "运营效率" },
  { value: "price-alert", label: "价格预警" },
];

const ScreenshotViewer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hotelId = searchParams.get("hotelId") || "";
  const componentType = searchParams.get("componentType") || "room-status";
  const storeName = searchParams.get("storeName") || "";

  const [imgError, setImgError] = useState(false);
  const [localHotelId, setLocalHotelId] = useState(hotelId);
  const [localComponentType, setLocalComponentType] = useState(componentType);
  const [localStoreName, setLocalStoreName] = useState(storeName);

  const params = new URLSearchParams();
  if (localHotelId) params.set("hotelId", String(localHotelId));
  if (localComponentType) params.set("componentType", localComponentType);
  if (localStoreName) params.set("storeName", localStoreName);
  const apiPath = `/api/store-revenue/component-screenshot?${params.toString()}`;
  const apiUrl = apiPath;
  const hasParams = Boolean(localHotelId && localComponentType);
  // central 当前仅 HTTP，直接打开图片时强制用 http 避免无响应
  const directImageUrl =
    typeof globalThis.window !== "undefined"
      ? `http://${globalThis.window.location.host}${apiPath}`
      : apiPath;

  const applyParams = () => {
    const next = new URLSearchParams();
    if (localHotelId) next.set("hotelId", String(localHotelId));
    if (localComponentType) next.set("componentType", localComponentType);
    if (localStoreName) next.set("storeName", localStoreName);
    setSearchParams(next, { replace: true });
    setImgError(false);
  };

  const openRawImage = () => {
    globalThis.window?.open(directImageUrl, "_blank");
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <Typography.Title level={4}>组件截图查看</Typography.Title>
      <Alert
        type="info"
        showIcon
        message="外部访问"
        description={
          <>
            请务必使用 <strong>http://</strong> 打开（当前 central 仅 HTTP）。首次截图约需 1–2 分钟，请耐心等待；若长时间空白请检查 nginx 对该接口超时（建议 ≥130s）。{" "}
            <Typography.Link copyable href={directImageUrl} target="_blank" rel="noopener noreferrer">
              {directImageUrl}
            </Typography.Link>
          </>
        }
        style={{ marginBottom: 16 }}
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <InputNumber
          min={1}
          placeholder="酒店 ID"
          value={localHotelId ? Number(localHotelId) : undefined}
          onChange={(v) => setLocalHotelId(v != null ? String(v) : "")}
        />
        <Select
          style={{ width: 160 }}
          placeholder="组件类型"
          value={localComponentType || undefined}
          onChange={setLocalComponentType}
          options={COMPONENT_OPTIONS}
        />
        <Input
          placeholder="storeName（可选）"
          value={localStoreName}
          onChange={(e) => setLocalStoreName(e.target.value)}
          style={{ width: 160 }}
        />
        <Button type="primary" onClick={applyParams}>
          应用并刷新
        </Button>
        <Button onClick={openRawImage}>在新标签页仅打开图片</Button>
      </Space>
      {!hasParams ? (
        <Alert type="warning" message="请填写酒店 ID 并选择组件类型后点击「应用并刷新」" />
      ) : (
        <div style={{ marginTop: 16 }}>
          {imgError && (
            <Alert
              type="error"
              message="图片加载失败"
              description="若页面显示 Cannot GET，说明 /api 未转发到 msCentral 后端，请在 nginx 中配置将 /api 反向代理到后端（端口 50045）。并确认后端 FRONTEND_BASE_URL 与该酒店有数据。"
              style={{ marginBottom: 16 }}
            />
          )}
          <img
            src={apiUrl}
            alt={`组件截图 ${componentType} ${hotelId}`}
            style={{ maxWidth: "100%", border: "1px solid #eee", borderRadius: 8 }}
            onLoad={() => setImgError(false)}
            onError={() => setImgError(true)}
          />
        </div>
      )}
    </div>
  );
};

export default ScreenshotViewer;
